import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashApi, clientsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  ShieldAlert, ArrowLeft, Receipt, Loader2, TrendingUp, Users, Wallet, Plus, Trash2, Pencil, X, CircleDollarSign,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { k: "active", label: "Actif", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  { k: "suspended", label: "Suspendu", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  { k: "cancelled", label: "Résilié", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
];
const statusMeta = (k) => STATUSES.find((s) => s.k === k) || STATUSES[0];
const fcfa = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " FCFA";
const emptyForm = { client_id: "", client_name: "", plan: "Standard", amount: "", status: "active", start_date: "" };

const Finance = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState("");

  const load = async () => {
    try {
      const [s, sub, cl] = await Promise.all([dashApi.financeStats(), dashApi.subscriptions(), clientsApi.list().catch(() => [])]);
      setStats(s); setSubs(sub); setClients(cl || []);
    } catch { toast.error("Impossible de charger les données financières"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reset = () => { setForm(emptyForm); setEditId(null); };

  const onPickClient = (e) => {
    const id = e.target.value;
    const c = clients.find((x) => x.id === id);
    setForm((f) => ({ ...f, client_id: id, client_name: c ? `${c.first_name} ${c.last_name}`.trim() : f.client_name }));
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setForm({ client_id: s.client_id || "", client_name: s.client_name || "", plan: s.plan || "Standard", amount: String(s.amount ?? ""), status: s.status || "active", start_date: (s.start_date || "").slice(0, 10) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) return toast.error("Nom du client requis");
    if (!form.amount || isNaN(Number(form.amount))) return toast.error("Montant mensuel invalide");
    setSaving(true);
    const payload = { ...form, amount: Number(form.amount) };
    try {
      if (editId) { await dashApi.updateSubscription(editId, payload); toast.success("Abonnement mis à jour"); }
      else { await dashApi.createSubscription(payload); toast.success("Abonnement créé"); }
      reset(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    setRemoving(s.id);
    try { await dashApi.removeSubscription(s.id); toast.success("Abonnement supprimé"); if (editId === s.id) reset(); load(); }
    catch { toast.error("Échec de la suppression"); }
    finally { setRemoving(""); }
  };

  const maxRev = useMemo(() => Math.max(1, ...(stats?.monthly || []).map((m) => m.revenue)), [stats]);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold leading-tight flex items-center gap-2"><Receipt className="w-4 h-4 text-emerald-400" /> Statistiques financières</h1>
            <p className="text-[11px] font-mono text-slate-500">Sama Jaamu — Abonnements & revenus</p>
          </div>
        </div>
        <Button data-testid="finance-back-btn" onClick={() => navigate("/")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center h-[60vh] text-slate-500"><Loader2 className="w-7 h-7 animate-spin" /></div>
      ) : (
        <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="finance-kpis">
            <Kpi icon={Wallet} color="text-emerald-400" label="Revenu mensuel (MRR)" value={fcfa(stats.mrr)} testid="kpi-mrr" />
            <Kpi icon={TrendingUp} color="text-yellow-400" label="Revenu annuel (ARR)" value={fcfa(stats.arr)} testid="kpi-arr" />
            <Kpi icon={Users} color="text-blue-400" label="Abonnés actifs" value={stats.active_count} testid="kpi-active" />
            <Kpi icon={CircleDollarSign} color="text-emerald-300" label="Revenu moyen / abonné" value={fcfa(stats.arpu)} testid="kpi-arpu" />
          </section>

          <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5">
            <h2 className="font-display font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> Évolution des revenus (6 derniers mois)</h2>
            <div className="h-64" data-testid="finance-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(v)} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 12, color: "#e2e8f0" }}
                    formatter={(v) => [fcfa(v), "Revenu"]}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5">
              <h2 className="font-display font-semibold mb-4">Répartition par formule</h2>
              {stats.by_plan.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun abonnement actif.</p>
              ) : (
                <div className="space-y-3" data-testid="plan-breakdown">
                  {stats.by_plan.map((p) => (
                    <div key={p.plan}>
                      <div className="flex justify-between text-sm mb-1"><span className="text-slate-300">{p.plan} <span className="text-slate-500">({p.count})</span></span><span className="font-mono text-emerald-300">{fcfa(p.mrr)}</span></div>
                      <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${(p.mrr / Math.max(1, stats.mrr)) * 100}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-5 pt-4 border-t border-slate-800 grid grid-cols-3 gap-2 text-center">
                <MiniStat label="Actifs" value={stats.active_count} color="text-emerald-400" />
                <MiniStat label="Suspendus" value={stats.suspended_count} color="text-amber-400" />
                <MiniStat label="Résiliés" value={stats.cancelled_count} color="text-red-400" />
              </div>
            </div>

            <div className="lg:col-span-2 bg-slate-900/70 border border-slate-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-semibold flex items-center gap-2">
                  {editId ? <Pencil className="w-4 h-4 text-yellow-400" /> : <Plus className="w-4 h-4 text-emerald-400" />}
                  {editId ? "Modifier l'abonnement" : "Nouvel abonnement"}
                </h2>
                {editId && <button data-testid="sub-cancel-edit" onClick={reset} className="text-slate-400 hover:text-slate-200"><X className="w-4 h-4" /></button>}
              </div>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Client</Label>
                  {clients.length > 0 && (
                    <select data-testid="sub-client-select" value={form.client_id} onChange={onPickClient} className="w-full h-10 rounded-md bg-slate-950 border border-slate-700 text-slate-100 px-3 text-sm mb-2">
                      <option value="">— Choisir un client existant —</option>
                      {clients.map((c) => <option key={c.id} value={c.id}>{`${c.first_name} ${c.last_name} · ${c.phone}`}</option>)}
                    </select>
                  )}
                  <Input data-testid="sub-client-name" value={form.client_name} onChange={set("client_name")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Nom du client" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Formule</Label>
                  <Input data-testid="sub-plan" value={form.plan} onChange={set("plan")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Standard" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Montant mensuel (FCFA)</Label>
                  <Input data-testid="sub-amount" type="number" value={form.amount} onChange={set("amount")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="5000" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Statut</Label>
                  <select data-testid="sub-status" value={form.status} onChange={set("status")} className="w-full h-10 rounded-md bg-slate-950 border border-slate-700 text-slate-100 px-3 text-sm">
                    {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Date de début</Label>
                  <Input data-testid="sub-start-date" type="date" value={form.start_date} onChange={set("start_date")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" />
                </div>
                <Button type="submit" data-testid="sub-save-btn" disabled={saving} className="col-span-2 h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Enregistrer" : "Ajouter l'abonnement")}
                </Button>
              </form>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-semibold">Abonnements</h2>
              <span className="font-mono text-xs text-slate-500" data-testid="subs-count">{subs.length} abonnement(s)</span>
            </div>
            {subs.length === 0 ? (
              <div className="bg-slate-900/60 border border-dashed border-slate-700 rounded-2xl p-10 text-center text-slate-500">
                <Receipt className="w-8 h-8 mx-auto mb-3 text-slate-600" />
                <p className="font-display font-semibold text-slate-300">Aucun abonnement</p>
                <p className="text-sm mt-1">Ajoutez un abonnement via le formulaire ci-dessus.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="subs-list">
                {subs.map((s) => {
                  const m = statusMeta(s.status);
                  return (
                    <div key={s.id} data-testid={`sub-row-${s.id}`} className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-display font-semibold text-slate-100 truncate">{s.client_name}
                          <span className={`text-[9px] font-mono border px-1.5 py-0.5 rounded-full ml-2 ${m.cls}`}>{m.label}</span>
                        </p>
                        <p className="text-xs font-mono text-slate-400 mt-0.5">{s.plan} · depuis {(s.start_date || "").slice(0, 10)}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono text-emerald-300 text-sm">{fcfa(s.amount)}<span className="text-slate-500">/mois</span></span>
                        <Button data-testid={`sub-edit-${s.id}`} onClick={() => startEdit(s)} size="icon" variant="ghost" className="text-slate-400 hover:text-yellow-400 hover:bg-yellow-950/30"><Pencil className="w-4 h-4" /></Button>
                        <Button data-testid={`sub-delete-${s.id}`} onClick={() => remove(s)} disabled={removing === s.id} size="icon" variant="ghost" className="text-slate-400 hover:text-red-400 hover:bg-red-950/40">{removing === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      )}
    </div>
  );
};

const Kpi = ({ icon: Icon, color, label, value, testid }) => (
  <div data-testid={testid} className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4">
    <Icon className={`w-5 h-5 ${color} mb-2`} />
    <p className={`font-display font-bold text-xl leading-tight ${color}`}>{value}</p>
    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-1">{label}</p>
  </div>
);

const MiniStat = ({ label, value, color }) => (
  <div>
    <p className={`font-display font-bold text-lg ${color}`}>{value}</p>
    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
  </div>
);

export default Finance;
